/**
 * Pure function to detect if a save operation should trigger a document rename.
 *
 * Checks if the DocType supports rename (allow_rename + autoname: field:X)
 * and if the user has changed the autoname field value.
 */

export interface RenameDetection {
  oldName: string;
  newName: string;
  autoNameField: string;
}

/**
 * Detect whether the form data represents a rename operation.
 *
 * @returns RenameDetection if rename is needed, null otherwise
 */
export function detectRename(
  metaSchema: { autoname?: string; allow_rename?: number | 0 | 1 },
  formData: Record<string, any>,
  docName: string
): RenameDetection | null {
  if (!metaSchema.allow_rename) return null;

  const autoname = metaSchema.autoname;
  if (!autoname || !autoname.startsWith("field:")) return null;

  const fieldname = autoname.split(":")[1];
  if (!fieldname) return null;

  const newName = typeof formData[fieldname] === "string"
    ? formData[fieldname].trim()
    : "";

  if (!newName) return null;

  // If the field value matches the document name exactly, no rename
  if (newName === docName) return null;

  return {
    oldName: docName,
    newName,
    autoNameField: fieldname,
  };
}
